import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserPreferences, selectUserPreferencesLoading, selectUserPreferencesSaving,
  loadUserPreferences, saveUserPreferences, updateUserPreferenceKey, clearUserPreferences,
} from '../redux/slices/userPreferencesSlice';

/**
 * UserPreferencesManager Component
 *
 * Displays and manages user preferences with tabbed interface including:
 * - Grid column layouts (with individual delete)
 * - VCX Events filter settings
 * - Other UI preferences
 *
 * Allows users to:
 * - View current preferences in organized tabs
 * - Delete individual grid layouts
 * - Delete specific preference categories
 * - Reset all preferences
 */
const UserPreferencesManager = () => {
  const dispatch = useDispatch();
  const preferences = useSelector(selectUserPreferences);
  const loading = useSelector(selectUserPreferencesLoading);
  const saving = useSelector(selectUserPreferencesSaving);

  const [activeTab, setActiveTab] = useState('grid_layouts'); // 'grid_layouts', 'filters', 'other'
  const [confirmDelete, setConfirmDelete] = useState(null); // Track which item needs confirmation
  const [confirmReset, setConfirmReset] = useState(null); // Track which section needs confirmation

  // Delete a specific grid layout
  const handleDeleteGridLayout = async (gridId) => {
    const confirmKey = `grid_${gridId}`;

    if (confirmDelete !== confirmKey) {
      setConfirmDelete(confirmKey);
      return;
    }

    try {
      const currentLayouts = preferences.ag_grid_layouts || {};
      const { [gridId]: removed, ...remainingLayouts } = currentLayouts;

      // Update Redux store
      dispatch(updateUserPreferenceKey({
        key: 'ag_grid_layouts',
        value: remainingLayouts
      }));

      // Save to backend - send ALL current preferences
      const updatedPreferences = {
        ...preferences,
        ag_grid_layouts: remainingLayouts
      };
      await dispatch(saveUserPreferences(updatedPreferences)).unwrap();

      setConfirmDelete(null);
    } catch (error) {
      console.error('Failed to delete grid layout:', error);
    }
  };

  // Delete a specific preference category
  const handleClearSection = async (sectionKey) => {
    if (confirmReset !== sectionKey) {
      setConfirmReset(sectionKey);
      return;
    }

    try {
      // Update Redux store
      dispatch(updateUserPreferenceKey({
        key: sectionKey,
        value: {}
      }));

      // Save to backend - send ALL current preferences
      const updatedPreferences = {
        ...preferences,
        [sectionKey]: {}
      };
      await dispatch(saveUserPreferences(updatedPreferences)).unwrap();

      setConfirmReset(null);
    } catch (error) {
      console.error('Failed to delete preference section:', error);
    }
  };

  // Reset all preferences
  const handleResetAll = async () => {
    if (confirmReset !== 'all') {
      setConfirmReset('all');
      return;
    }

    try {
      // Delete all preferences
      dispatch(clearUserPreferences());

      // Save empty preferences to backend
      await dispatch(saveUserPreferences({})).unwrap();

      setConfirmReset(null);
    } catch (error) {
      console.error('Failed to delete all preferences:', error);
    }
  };

  // Refresh preferences from backend
  const handleRefresh = () => {
    dispatch(loadUserPreferences());
  };

  // Format preference value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Render Grid Layouts Tab
  const renderGridLayoutsTab = () => {
    const layouts = preferences.ag_grid_layouts || {};
    const hasLayouts = Object.keys(layouts).length > 0;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">
              Grid Column Layouts
            </h5>
            <p className="text-muted small mb-0">
              Manage saved table column layouts for different grids
            </p>
          </div>
          {hasLayouts && (
            <button
              className={`btn btn-sm ${confirmReset === 'ag_grid_layouts' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => handleClearSection('ag_grid_layouts')}
              disabled={saving}
            >
              <i className="fas fa-trash-alt me-1"></i>
              {confirmReset === 'ag_grid_layouts' ? 'Sure to delete all?' : 'Delete All'}
            </button>
          )}
        </div>

        {hasLayouts ? (
          <div className="row g-3">
            {Object.entries(layouts).map(([gridName, layout]) => {
              const confirmKey = `grid_${gridName}`;
              const isConfirmingDelete = confirmDelete === confirmKey;

              return (
                <div key={gridName} className="col-12 col-md-6">
                  <div className="card border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="text-capitalize mb-0">
                          {gridName.replace(/_/g, ' ')}
                        </h6>
                        <button
                          className={`btn btn-sm ${isConfirmingDelete ? 'btn-danger' : 'btn-outline-danger'}`}
                          onClick={() => handleDeleteGridLayout(gridName)}
                          disabled={saving}
                          title={isConfirmingDelete ? 'Click again to confirm' : 'Delete this layout'}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>

                      {layout.columnState && (
                        <div>
                          <small className="text-muted d-block mb-2">
                            <i className="fas fa-columns me-1"></i>
                            {layout.columnState.length} columns configured
                          </small>
                          <details open>
                            <summary className="text-primary" style={{ cursor: 'pointer' }}>
                              <small>View Column Details</small>
                            </summary>
                            <div className="mt-2">
                              <div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                <table className="table table-sm table-bordered small">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Column ID</th>
                                      <th>Width</th>
                                      <th>Visible</th>
                                      <th>Pinned</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {layout.columnState.map((col, idx) => (
                                      <tr key={idx}>
                                        <td><code className="small">{col.colId}</code></td>
                                        <td>{col.width || 'auto'}</td>
                                        <td>
                                          <i className={`fas fa-${col.hide ? 'eye-slash text-muted' : 'eye text-success'}`}></i>
                                        </td>
                                        <td>{col.pinned || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-table fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No Saved Grid Layouts</h6>
            <p className="text-muted small mb-0">
              Customize column layouts on any grid page and save them to appear here
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render VCX Events Filters Tab
  const renderFiltersTab = () => {
    const filters = preferences.vcx_events_filters || {};
    const hasFilters = Object.keys(filters).length > 0;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">
              VCX Events Filters
            </h5>
            <p className="text-muted small mb-0">
              Saved default filter settings for VCX Events page
            </p>
          </div>
          {hasFilters && (
            <button
              className={`btn btn-sm ${confirmReset === 'vcx_events_filters' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => handleClearSection('vcx_events_filters')}
              disabled={saving}
            >
              <i className="fas fa-trash-alt me-1"></i>
              {confirmReset === 'vcx_events_filters' ? 'Sure to delete?' : 'Delete'}
            </button>
          )}
        </div>

        {hasFilters ? (
          <div className="row g-3">
            {Object.entries(filters).map(([key, value]) => (
              <div key={key} className="col-md-6">
                <div className="card border">
                  <div className="card-body">
                    <small className="text-muted d-block fw-bold text-capitalize mb-1">
                      {key.replace(/_/g, ' ')}
                    </small>
                    <code className="small">{formatValue(value)}</code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-filter fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No Saved Filters</h6>
            <p className="text-muted small mb-0">
              Your default filter settings will appear here when you save them on the VCX Events page
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render Other Preferences Tab
  const renderOtherTab = () => {
    const knownKeys = ['vcx_events_filters', 'ag_grid_layouts'];
    const otherPrefs = Object.keys(preferences).filter(key => !knownKeys.includes(key));
    const hasOther = otherPrefs.length > 0;

    return (
      <div>
        <div className="mb-3">
          <h5 className="mb-1">
            Other Preferences
          </h5>
          <p className="text-muted small mb-0">
            Additional UI settings and preferences
          </p>
        </div>

        {hasOther ? (
          <div className="row g-3">
            {otherPrefs.map((key) => (
              <div key={key} className="col-12">
                <div className="card border">
                  <div className="card-body">
                    <small className="text-muted d-block fw-bold mb-1">{key}</small>
                    <pre className="small mb-0 p-2 bg-light rounded">{formatValue(preferences[key])}</pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-cog fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No Other Preferences</h6>
            <p className="text-muted small mb-0">
              Additional preferences will appear here when saved
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading preferences...</span>
        </div>
        <p className="text-muted small mt-2">Loading preferences...</p>
      </div>
    );
  }

  const hasAnyPreferences = Object.keys(preferences).length > 0;
  const gridLayoutsCount = Object.keys(preferences.ag_grid_layouts || {}).length;
  const filtersCount = Object.keys(preferences.vcx_events_filters || {}).length;
  const knownKeys = ['vcx_events_filters', 'ag_grid_layouts'];
  const otherCount = Object.keys(preferences).filter(key => !knownKeys.includes(key)).length;

  return (
    <div className="user-preferences-manager">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-muted mb-0">
            Manage your saved UI settings and preferences
          </p>
        </div>
        <div>
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={handleRefresh}
            disabled={loading || saving}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-1`}></i>
            Refresh
          </button>
          {hasAnyPreferences && (
            <button
              className={`btn btn-sm ${confirmReset === 'all' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={handleResetAll}
              disabled={saving}
            >
              <i className="fas fa-trash-alt me-1"></i>
              {confirmReset === 'all' ? 'Sure to delete all preferences?' : 'Delete All'}
            </button>
          )}
        </div>
      </div>

      {/* Save Status */}
      {saving && (
        <div className="alert alert-info py-2 mb-3">
          <i className="fas fa-spinner fa-spin me-2"></i>
          Saving preferences...
        </div>
      )}

      {/* Confirmation Cancel */}
      {(confirmDelete || confirmReset) && (
        <div className="alert alert-warning py-2 mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Click again to confirm, or{' '}
          <button
            className="btn btn-link btn-sm p-0"
            onClick={() => {
              setConfirmDelete(null);
              setConfirmReset(null);
            }}
          >
            cancel
          </button>
        </div>
      )}

      {hasAnyPreferences ? (
        <>
          {/* Tabs Navigation */}
          <ul className="nav nav-tabs mb-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'grid_layouts' ? 'active' : ''}`}
                onClick={() => setActiveTab('grid_layouts')}
                type="button"
              >
                <i className="fas fa-table me-2"></i>
                Grid Layouts
                {gridLayoutsCount > 0 && (
                  <span className="badge bg-primary ms-2">{gridLayoutsCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'filters' ? 'active' : ''}`}
                onClick={() => setActiveTab('filters')}
                type="button"
              >
                <i className="fas fa-filter me-2"></i>
                Filters
                {filtersCount > 0 && (
                  <span className="badge bg-primary ms-2">{filtersCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'other' ? 'active' : ''}`}
                onClick={() => setActiveTab('other')}
                type="button">
                <i className="fas fa-cog me-2"></i>
                Other
                {otherCount > 0 && (
                  <span className="badge bg-primary ms-2">{otherCount}</span>
                )}
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'grid_layouts' && renderGridLayoutsTab()}
            {activeTab === 'filters' && renderFiltersTab()}
            {activeTab === 'other' && renderOtherTab()}
          </div>
        </>
      ) : (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No Saved Preferences</h6>
            <p className="text-muted small mb-0">
              Your UI settings will appear here once you customize filters or column layouts
            </p>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="mt-4">
        <small className="text-muted">
          <i className="fas fa-info-circle me-1"></i>
          Preferences are automatically saved when you adjust filters or column layouts.
          They persist across sessions and devices.
        </small>
      </div>
    </div>
  );
};

export default UserPreferencesManager;
